import React, { useState, useEffect, useContext, ReactNode } from "react";
import {
  Card,
  CardTitle,
  CardText,
  DataTable,
  TableBody,
  TableRow,
  TableColumn,
} from "react-md";
import { cloneDeep } from "lodash";
import {
  normalizeNumberVariableDecimal,
  normalizePhone,
  normalizeIntegerNegativeOrPositive,
} from "components/FormElements";
import { MField, MAction, MSpinner } from "components/Shared";
import * as yup from "yup";
import {
  DATA_TYPE_NUMBER,
  DATA_TYPE_MULTI_LINE_STRING,
  DATA_TYPE_NUMBER_UNFORMATTED,
  DATA_TYPE_PERCENT,
  DATA_TYPE_PERCENT_WHOLE_NUMBER,
  DATA_TYPE_PERCENT_DECIMAL,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_MONEY,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_DATE,
  DATA_TYPE_DATE_TIME,
  DATA_TYPE_TIME,
  DATA_TYPE_DAYS_ELAPSED,
  DATA_TYPE_DROPDOWN,
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_EMAIL,
  DATA_TYPE_APPRAISER,
  DATA_TYPE_ADDRESS,
  DATA_TYPE_COUNTY,
  DATA_TYPE_GEOCODE,
  DATA_TYPE_DATE_DOW,
  DATA_TYPE_DATE_TIME_DOW,
  DATA_TYPE_HYPERLINK,
  DATA_TYPE_JSX,
  DATA_TYPE_PASSWORD,
  DATA_TYPE_CREDIT_CARD,
  DATA_TYPE_NUMBER_STRING,
  DATA_TYPE_NUMBER_NEGATIVE_OR_POSITIVE,
} from "typeConstants/dataTypes";
import {
  booleanToYesNo,
  dateTimeToStringWithNumOfDays,
  moneyFormatter,
  dateToStringYy,
  dateTimeToString,
  dateToStringWithDOW,
  formatPercent,
  formatPercentWholeNumber,
  emailHyperlink,
  mapAddressHyperlink,
  mapCountyHyperlink,
  mapGeocodeHyperlink,
  dateTimeToTime,
  appraiserFormatter,
  dateTimeToStringWithDOW,
  makeHyperlink,
  checkIfEmpty,
  formatDecimalPercent2,
} from "components/Shared/Formatters";
import { NOT_PROVIDED } from "typeConstants/stringConstants";
import ReactTooltip from "react-tooltip";
import { DialogContext } from "components/Shared/DialogProvider";
import { classes } from "components/Utility";

export interface LabelValue {
  label: string;
  value: any;
}

interface MCardFieldDataTypeOptions {
  /** if not specified any amount is allowed, only used for DATA_TYPE_NUMBER */
  numDigitsAfterDecimal?: number;
  /**array of { label, value } or just array of strings (labels) to display in a drop down edit list if the dataType is DATA_TYPE_DROPDOWN, value will be returned unless it is undefined, then label will be returned */
  options?: LabelValue[] | string[];
  /**true by default, if false the clear value icon will not show for DATA_TYPE_DATE, DATA_TYPE_DATE_TIME, DATA_TYPE_DAYS_ELAPSED, DATA_TYPE_DROPDOWN */
  nullable?: boolean;
  /**by default not set, instanceOf(Date), if DATA_TYPE_DATE, DATA_TYPE_DATE_TIME, DATA_TYPE_DAYS_ELAPSED min and max dates will set the boundaries for the day selection from datepicker */
  minDate?: Date;
  /**by default not set, instanceOf(Date), if DATA_TYPE_DATE, DATA_TYPE_DATE_TIME, DATA_TYPE_DAYS_ELAPSED min and max dates will set the boundaries for the day selection from datepicker */
  maxDate?: Date;
  /**if using DATA_TYPE_DROPDOWN2 this is the sortFunc that will get passed to MSelect to sort the dropdown */
  sortFunc?: any; // TODO: fix this
  /**if using DATA_TYPE_DROPDOWN2, it makes it a multi select */
  isMultiSelect?: boolean;
  /**if using DATA_TYPE_DROPDOWN2, set this to true to show a select all option and not display the selected items in the field after blur */
  isShortMulti?: boolean;
  /** if using DATA_TYPE_ADDRESS_AUTOCOMPLETE, set this to true to show only the street address. Otherwise the whole address will be shown */
  streetAddressOnly?: boolean;
  /**if using DATA_TYPE_DROPDOWN2, this will allow the user to add an item to the drop down. Note: if using creatable, your values in the options must be strings as the component calls .toLowerCase on the value */
  creatable?: boolean;
  /**if using DATA_TYPE_MULTI_LINE_STRING, pass true to wrap the text to multiple lines if needed */
  wrapMultiLineText?: boolean;
}

export type MCardDataTypes = typeof DATA_TYPE_NUMBER | typeof DATA_TYPE_MULTI_LINE_STRING | typeof DATA_TYPE_NUMBER_UNFORMATTED | typeof DATA_TYPE_PERCENT | typeof DATA_TYPE_PERCENT_WHOLE_NUMBER | typeof DATA_TYPE_PERCENT_DECIMAL | typeof DATA_TYPE_PHONE_NUMBER | typeof DATA_TYPE_MONEY | typeof DATA_TYPE_BOOLEAN | typeof DATA_TYPE_DATE | typeof DATA_TYPE_DATE_TIME | typeof DATA_TYPE_TIME | typeof DATA_TYPE_DAYS_ELAPSED | typeof DATA_TYPE_DROPDOWN | typeof DATA_TYPE_DROPDOWN2 | typeof DATA_TYPE_EMAIL | typeof DATA_TYPE_APPRAISER | typeof DATA_TYPE_ADDRESS | typeof DATA_TYPE_COUNTY | typeof DATA_TYPE_GEOCODE | typeof DATA_TYPE_DATE_DOW | typeof DATA_TYPE_DATE_TIME_DOW | typeof DATA_TYPE_HYPERLINK | typeof DATA_TYPE_JSX | typeof DATA_TYPE_PASSWORD | typeof DATA_TYPE_CREDIT_CARD | typeof DATA_TYPE_NUMBER_STRING | typeof DATA_TYPE_NUMBER_NEGATIVE_OR_POSITIVE;

/**Note: validation only looks at controls that have a key property provided in the fields */
export interface MCardFieldSimple {
  /**optional - (required if using control field or if you need validation) used for the unique react key for the collection of objects rendered to the screen. if not provided label will be used for a key. if using the control field you need to provide a key because you wont be providing a label, also used for the data return if the card is editable */
  key?: string;
  /**optional - not needed if control is specified */
  label?: string;
  /**optional - will be rendered onto the columns as className-field and className-value, the row will also get the style className-row */
  className?: string;
  /**
   * Accepts dates, strings, numbers, and booleans
   * If using DATA_TYPE_DROPDOWN pass { label, value }
   * If using  pass { label, onClick } onClick can be a function or a string, if its a string its just the address to navigate to
   */
  value?: any; // TODO: fix this
  /** one of the exported DATA_TYPE_... defines how the card data is formatted and how it is edited */
  dataType?: MCardDataTypes;
  /**not required, options for certain data types */
  dataTypeOptions?: MCardFieldDataTypeOptions;
  /**optional - yup validation line for this field, ex: yup.number("Field must be a number").positive("Field must be positive").integer("Field must be an integer") (with: import * as yup from "yup")
   * Note: yup.when() does not appear to work with the current implementation of MCard
   * Note 2: This requires the key property to be set
   */
  validation?: any; // TODO: fix this
  /**optional - if true then no value will be displayed and the cell will get a className of "card-field-label-only", will appear "bold"
   * if you want to use a label to display validation, give it the className "card-error" and it will appear the same as validation messages
   */
  isLabelOnly?: boolean;
  /**optional - if true then will get a className of "card-field-indented", the field will shift 30px to the left */
  isIndented?: boolean;
  /**optional - set of MCard props to render an MCard tooltip on field hover */
  tooltip?: MCardProps;
  /**optional - by default cards are not editable but the isEditable prop is true then all fields are editable by default. DATA_TYPE_HYPERLINK is not editable. */
  editable?: boolean;
  /**optional - array of fields (no support for controls) that show on edit instead of this field, ex: field is Name - First + Last, edit fields are First Name, Last Name, this allows us to display two fields as one but edit them separately */
  editFields?: MCardFieldSimple[];
  /**optional - callback function when field is clicked */
  onClick?: any; // TODO: fix this
  /**optional - callback when value is changed while editing */
  onChange?: any; // TODO: fix this
  /**Field placeholder */
  placeholder?: string; // TODO: is this being set anywhere?
  /**optional - if true this field wont show up when we are editing */
  hideInEditMode?: boolean;
  /**Function to add conditional class to the value */
  conditionalClasses?: (field: MCardFieldSimple) => string; // TODO: wtf is this?
}

export interface MCardField extends MCardFieldSimple {
  /**optional - if provided the card will just render the control provided here and it will be allowed to span both columns */
  control?: React.ReactNode;
}

export interface KeyValueOriginal<T = any> {
  key: keyof T;
  value: any;
  originalValue: any;
}

export interface KeyValueOriginalError extends KeyValueOriginal {
  error: any; // TODO: fix this
}

export interface MCardAction {
  /**material design icon */
  icon: string;
  /**tooltip for action */
  helpText?: string;
  /**Click event */
  onClick: () => void;
  /**Display order */
  order?: number;
}

export interface MCardControlMethods {
  /**Runs validation, show errors on the form, and return true if validated, false otherwise. validateAllFields defaults to false, only fields that have been edited will be validated by default */
  runValidation: (validateAllFields?: boolean) => boolean;
  /**Clears all validation errors */
  clearValidationErrors: () => void;
  /**Returns an array of objects for each edited field. To get all fields, whether edited or not, pass in a boolean value of true */
  getEditedData: (returnAllFields?: boolean) => KeyValueOriginalError[];
  /**Returns a single object with key value pairs for each edited field. To get all fields, whether edited or not, pass in a boolean value of true */
  getEditedDataObject: (returnAllFields?: boolean) => Record<string, string>;
  /**Calls MField control method to set the value for the given key */
  setEditData: (key: string, value: any, option?: string) => void;
  /**Updates editData without destroying existing data */
  refreshEditData: () => void;
}

interface MCardEditDataField {
  key: string;
  value: any;
  originalValue: any;
  error: string;
  dataType: MCardDataTypes;
  editable: boolean;
  edited: boolean;
  onChange: () => void;
}

export interface MCardProps {
  /**Card title */
  title: string | ReactNode;
  /**Class attached to the card */
  className?: string;
  /**Class attached to the cards content container */
  cardContentClassName?: string;
  /**Cell width size if you want the card to be a part of an md-cell */
  cellWidth?: number;
  /**Style for when you need to do math for dynamic styling */
  style?: string;
  /**Shows a collapse icon for the card if true */
  isCollapsible?: boolean,
  /**Card will start in the collapsed state if true */
  startCollapsed?: boolean,
  /**Fields to render */
  fields?: MCardField[];
  /**Specifying children and not fields will just put the child control inside of the card */
  children?: React.ReactNode;
  /**Renders children in a div instead of a card section. This is because someone made super stupid styles that all have !important on them targeting card-text and theres no way to remove that style */
  renderChildrenInDiv?: boolean;
  /**Default false, set true if you want the card to be editable */
  isEditable?: boolean;
  /**onlyEditable forces the card to be only editable
       there are no icons to enter edit mode or cancel edit mode
       IMPORTANT NOTE: in only editable mode, onEditFinished will be called every time something is changed
      please do not do database calls in onEditFinished in this case */
  onlyEditable?: boolean;
  /**Callback that gets called when edit is clicked */
  onEditStarted?: () => void;
  /**Callback that gets called when cancel edit is clicked */
  onEditCancelled?: () => void;
  /**callback called when a card is edited and apply is selected. only the keys that have been edited will be provided. if keys were not provided, the labels will be provided as a key instead */
  onEditFinished?: (changeArray: KeyValueOriginal[], changeMap: Record<string, string>) => boolean | void;
  /**callback called every time a field is changed. only the keys that have been edited will be provided. if keys were not provided, the labels will be provided as a key instead */
  onEditChanged?: (changeArray: KeyValueOriginal[], changeMap: Record<string, string>) => void;
  /**Use this if you want the values to display below the fields */
  singleColumn?: boolean;
  /**Use this if you want a custom element in the MCard Header (left of actions) */
  headerContent?: React.ReactNode;
  /**Use this if you want custom icons in the MGrid Header */
  actions?: MCardAction[];
  /**Use this to start the card in edit mode instead of readonly */
  startInEditMode?: boolean;
  /**disable hover styles */
  disableHoverStyles?: boolean;
  /**show an MSpinner is we are loading */
  isFetching?: boolean;
  /**show hover text on MCard title */
  toolTipTitle?: string;
  /**allows multiple columns */
  allowMultipleColumns?: boolean;
  /**this callback function will be called with an object that has multiple functions on it that let you control MCard */
  getControlMethods?: (methods: MCardControlMethods) => void;
}

const MCard = ({
  title,
  className,
  cardContentClassName,
  cellWidth,
  style,
  isCollapsible,
  startCollapsed,
  fields,
  children,
  renderChildrenInDiv,
  isEditable,
  onlyEditable,
  onEditStarted,
  onEditCancelled,
  onEditFinished,
  onEditChanged,
  singleColumn,
  headerContent,
  actions,
  startInEditMode,
  disableHoverStyles,
  isFetching,
  toolTipTitle,
  allowMultipleColumns,
  getControlMethods,
}: MCardProps) => {

  const [isEditing, setIsEditing] = useState<boolean>(onlyEditable || startInEditMode);
  const [editData, setEditData] = useState<MCardEditDataField[]>(null);
  const [fieldControlMethods] = useState({});
  const [runEditCallbacks, setRunEditCallbacks] = useState<string>(null);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(startCollapsed);

  const dialog = useContext(DialogContext);

  useEffect(() => {
    if (!isFetching && (onlyEditable || startInEditMode)) {
      setEditData(generateEditData());
    }
  }, [isFetching]);

  useEffect(() => {
    if (getControlMethods) {
      getControlMethods({
        runValidation: validateAll =>
          isEditing ? runValidation(validateAll) : true,
        clearValidationErrors: () => {
          if (isEditing) clearValidationErrors();
        },
        getEditedData: returnAllFields => isEditing ? getEditedDataObject(returnAllFields).editedData : null,
        getEditedDataObject: returnAllFields => isEditing ? getEditedDataObject(returnAllFields).editedDataObjectForm : null,
        setEditData: (key, value, option) => {
          if (!isEditing) return;
          if (typeof fieldControlMethods[key]?.setValue === "function") {
            if (option === "setOption") fieldControlMethods[key].setInitialObjectValue(value);
            else {
              fieldControlMethods[key].setValue(value);
              updateEditData(key, value);
            }
          }
        },
        refreshEditData: () => {
          let currentEditData = getClonedEditData();
          let newEditData = generateEditData();
          if (currentEditData && newEditData) {
            newEditData.forEach(n => {
              if (!currentEditData.some(c => c.key === n.key)) {
                currentEditData.push(n);
              }
            });
            setEditData(currentEditData);
          }
        }
      });
    }
  }); // no dependencies, this needs to run on every render so parent components are referencing the correct MCard reference......

  useEffect(() => {
    if (runEditCallbacks) {
      if (onlyEditable) callEditFinished();
      callEditChanged();
      if (validateAfter(runEditCallbacks)) runValidation();
      setRunEditCallbacks(null);
    }
  }, [runEditCallbacks]);

  const getKey = (field: MCardField) => {
    return field.key || field.label;
  };

  const getRawValue = (field: MCardField) => {
    switch (field.dataType) {
      case DATA_TYPE_DROPDOWN:
        return field.value && field.value.value;
      default:
        return field.value;
    }
  };

  const mapFieldToEditData = (field: MCardField) => {
    const mfield: MCardEditDataField = {
      key: getKey(field),
      value: getRawValue(field),
      // Purposely leave previousValue undefined
      originalValue: getRawValue(field),
      error: null,
      dataType: field.dataType,
      // If editable is not specified it will default to true
      editable: field.editable === undefined || field.editable,
      edited: false,
      onChange: field.onChange
    };
    return mfield;
  };

  const generateEditData = () => {
    return fields
      ?.flat()
      .reduce(
        (acc, f) => acc.concat((f.editFields || [f]).map(mapFieldToEditData)),
        []
      );
  };

  const renderTable = (fields: MCardField[]) => {
    let rows;

    if (!fields)
      return;

    if (!isEditing) {
      rows = fields.map(fieldToRow);
    } else {
      rows = fields
        .reduce((acc, f) => acc.concat(f.editFields || f), [])
        .map(fieldToRow);
    }

    if (singleColumn) rows = rows.flat(); //fieldToRow returns two rows per field if singleColumn is true

    return (
      <DataTable plain>
        <TableBody>{rows}</TableBody>
      </DataTable>
    );
  };

  const multiLineFormatter = (wrapMultiLineText: boolean) => (value: string) => {
    return value
      ? value
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n")
        .map((s, i) => <div key={`line_${i}`} style={wrapMultiLineText ? { whiteSpace: "wrap" } : { whiteSpace: "pre" }}>{s}</div>)
      : "";
  };

  const getRowClassName = (field: MCardField) => {
    let classes = [field.className
      ? field.className.endsWith("has-value")
        ? field.value ? `${field.className}-row` : ""
        : `${field.className}-row`
      : ""];
    if (disableHoverStyles)
      classes.push("disable-hover-styles");
    return classes.join(" ");
  };

  const setControlMethods = (key: string, controlMethods: any) => { // TODO: fix
    fieldControlMethods[key] = controlMethods;
  };

  // Need to validate these types after update as the onBlur will not work as expected (or there is no onBlur prop)
  const validateAfter = (dataType: string) => {
    switch (dataType) {
      case DATA_TYPE_DROPDOWN:
      case DATA_TYPE_DROPDOWN2:
      case DATA_TYPE_BOOLEAN:
      case DATA_TYPE_DATE_TIME:
      case DATA_TYPE_DAYS_ELAPSED:
      case DATA_TYPE_DATE:
      case DATA_TYPE_JSX:
        return true;
      default:
        return false;
    }
  };

  /* ORIGINAL:
  updateEditData(key, newValue) {
    let validateField = {};
    // this setState is being called and the callback is also called, but the changes werent being applied, wrapping in a set timeout to make it async worked.... sometimes ya just gotta
    setTimeout(() => {
      this.setState(state => {
        let field = state.editData.find(e => e.key === key);
        if (field) {
          field.value = newValue;
          field.edited = true;
          validateField = field;
          return { editData: state.editData };
        } return state;
      }, () => {
        if (this.props.onlyEditable) this.callEditFinished();
        this.callEditChanged();
        if (this.validateAfter(validateField.dataType)) this.runValidation();
      });
    }, 0);
  }
  */
  const updateEditData = (key: string, newValue: any) => {
    setEditData(data => {
      const nextData = cloneDeep(data);
      const field = nextData.find(e => e.key === key);
      if (field) {
        field.value = newValue;
        field.edited = true;
        setRunEditCallbacks(field.dataType || "DATA_TYPE_STRING"); // make sure we provide a default value for the data type because null/undefined was causing issues
      }
      return nextData;
    });
  };

  const getEditControl = (key: string, field: MCardField, editDataField: any) => {
    return (
      <MField
        id={key}
        className={field.className}
        dataType={field.dataType}
        dataTypeOptions={{ ...field.dataTypeOptions }}
        value={editDataField?.value}
        onChange={(k, v) => { updateEditData(k, v); if (field.onChange) field.onChange(v); }}
        getControlMethods={setControlMethods}
        ariaLabel={field.label}
        onBlur={() => runValidation()}
      />
    );
  };

  const getValue = (field: MCardField, value?: any) => {
    let valueFormatter;
    if (value === undefined) {
      value = getRawValue(field);
    }

    switch (field.dataType) {
      case DATA_TYPE_DROPDOWN:
      case DATA_TYPE_DROPDOWN2:
        return field?.value?.label || null;
      case DATA_TYPE_NUMBER:
        valueFormatter = normalizeNumberVariableDecimal(
          field.dataTypeOptions
            ? field.dataTypeOptions.numDigitsAfterDecimal
            : null
        );
        break;
      case DATA_TYPE_NUMBER_UNFORMATTED:
      case DATA_TYPE_NUMBER_STRING:
        valueFormatter = normalizeNumberVariableDecimal(0, true);
        break;
      case DATA_TYPE_NUMBER_NEGATIVE_OR_POSITIVE:
        valueFormatter = normalizeIntegerNegativeOrPositive;
        break;
      case DATA_TYPE_MONEY:
        valueFormatter = moneyFormatter(field.dataTypeOptions
          ? field.dataTypeOptions.numDigitsAfterDecimal
          : null);
        break;
      case DATA_TYPE_BOOLEAN:
        valueFormatter = booleanToYesNo;
        break;
      case DATA_TYPE_DATE:
        valueFormatter = dateToStringYy;
        break;
      case DATA_TYPE_DATE_TIME:
        valueFormatter = dateTimeToString;
        break;
      case DATA_TYPE_TIME:
        valueFormatter = dateTimeToTime;
        break;
      case DATA_TYPE_DAYS_ELAPSED:
        valueFormatter = dateTimeToStringWithNumOfDays;
        break;
      case DATA_TYPE_DATE_DOW:
        valueFormatter = dateToStringWithDOW;
        break;
      case DATA_TYPE_DATE_TIME_DOW:
        valueFormatter = dateTimeToStringWithDOW;
        break;
      case DATA_TYPE_PHONE_NUMBER:
        valueFormatter = normalizePhone;
        break;
      case DATA_TYPE_MULTI_LINE_STRING:
        valueFormatter = multiLineFormatter(field?.dataTypeOptions?.wrapMultiLineText ?? false);
        break;
      case DATA_TYPE_PERCENT:
        valueFormatter = formatPercent;
        break;
      case DATA_TYPE_PERCENT_WHOLE_NUMBER:
        valueFormatter = formatPercentWholeNumber;
        break;
      case DATA_TYPE_PERCENT_DECIMAL:
        valueFormatter = formatDecimalPercent2(0, true);
        break;
      case DATA_TYPE_EMAIL:
        valueFormatter = emailHyperlink;
        break;
      case DATA_TYPE_PASSWORD:
        valueFormatter = checkIfEmpty;
        break;
      case DATA_TYPE_ADDRESS:
        valueFormatter = mapAddressHyperlink;
        break;
      case DATA_TYPE_COUNTY:
        valueFormatter = mapCountyHyperlink;
        break;
      case DATA_TYPE_GEOCODE:
        valueFormatter = mapGeocodeHyperlink;
        break;
      case DATA_TYPE_APPRAISER:
        valueFormatter = appraiserFormatter;
        break;
      case DATA_TYPE_HYPERLINK:
        value = makeHyperlink(field.value.label, field.value.onClick); // hyperlink: { label, onClick }
        return value;
      case DATA_TYPE_JSX:
        value = field.value;
        return value;
      case DATA_TYPE_CREDIT_CARD:
        value = field.value;
        return value;
    }

    if (valueFormatter) return valueFormatter(value);
    return value === null || value === undefined ? "" : `${value}`; //cast value to string mainly because 0's are falsey and mess up some other logic whereas "0" is not falsey
  };

  const getLabelClassName = (field: MCardField) => {
    let classes = ["card-field"];
    if (field.className) classes.push(`${field.className}-field`);
    if (field.isLabelOnly) classes.push("card-field-label-only");
    if (field.isIndented) classes.push("card-field-indented");
    return classes.join(" ");
  };

  const getValueClassName = (field: MCardField) => {
    let classes = [field.className ? (
      `card-value ${field.className}-value`
    ) : (
      "card-value"
    )];

    if (field.conditionalClasses)
      classes = classes.concat(field.conditionalClasses(field));

    return classes.join(" ");
  };

  const getErrorClassName = (field: MCardField) => {
    return field.className
      ? `card-error ${field.className}-error`
      : "card-error";
  };

  const getTooltip = (field: MCardField, tooltipId: string) => {
    return (
      <TableColumn key={`${getKey(field)}_tooltip`} className={getLabelClassName(field)}>
        <ReactTooltip
          data-tip
          data-for={tooltipId}
          id={tooltipId}
          effect="solid"
          place="right"
          className="mcard-tooltip"
          delayHide={50}
          scrollHide={true}
          border={false}
        >
          <MCard {...field.tooltip} />
        </ReactTooltip>
      </TableColumn>
    );
  };

  const fieldToRow = (field: MCardField) => {
    const key = getKey(field);
    const editDataField = editData !== null && editData.find(ed => ed.key == key);

    if (field.hideInEditMode && (isEditing || onlyEditable)) return null;

    if (field.control)
      return (
        <TableRow
          className={getRowClassName(field)}
          key={key}
        >
          <TableColumn colSpan={2}>{field.control}</TableColumn>
        </TableRow>
      );

    let value;
    let notProvidedClass = "";
    if ((isEditing || onlyEditable) && field.editable !== false && field.dataType !== DATA_TYPE_JSX) {
      value = getEditControl(key, field, editDataField);
    } else {
      value = getValue(field, field.value);
      if (!value || field.value === null) {
        value = NOT_PROVIDED;
        notProvidedClass = "not-provided";
      }
    }

    if (field.isLabelOnly) value = "";
    const isTooltip = !!field.tooltip;
    const tooltipId = isTooltip && `${getKey(field)}-tooltip`;

    let lblCol = (
      <TableColumn key={`${getKey(field)}_label`} className={getLabelClassName(field)} colSpan={field.isLabelOnly ? 2 : 1}>
        {field.label}
      </TableColumn>
    );

    let valCol = field.isLabelOnly ? null : (
      <TableColumn key={`${getKey(field)}_value`} className={`${getValueClassName(field)} ${notProvidedClass}`}>
        {value}
      </TableColumn>
    );

    let rows;
    if (singleColumn) {
      rows = [
        <TableRow
          data-tip={isTooltip}
          data-for={tooltipId}
          onClick={field.onClick}
          className={getRowClassName(field)}
          key={`${getKey(field)}_field`}
        >
          <>
            {lblCol}
            {isTooltip && getTooltip(field, tooltipId)}
          </>
        </TableRow>,
        <TableRow
          data-tip={isTooltip}
          data-for={tooltipId}
          onClick={field.onClick}
          className={getRowClassName(field)}
          key={`${getKey(field)}_value`}
        >
          {valCol}
        </TableRow>
      ];
    } else {
      rows = [
        <TableRow
          data-tip={isTooltip}
          data-for={tooltipId}
          onClick={field.onClick}
          className={getRowClassName(field)}
          key={getKey(field)}
        >
          <>
            {[lblCol, valCol, isTooltip && getTooltip(field, tooltipId)]}
          </>
        </TableRow>
      ];
    }

    if (isEditing && editDataField && editDataField.error)
      rows.push(
        <TableRow
          className={getRowClassName(field)}
          key={`${getKey(field)}_error`}
        >
          <TableColumn colSpan={2} className={getErrorClassName(field)}>
            {editDataField.error}
          </TableColumn>
        </TableRow>
      );
    return rows;
  };

  const getClonedEditData = () => {
    return cloneDeep(editData);
  };

  const clearValidationErrors = () => {
    let nextEditData = getClonedEditData();
    nextEditData.forEach(ed => (ed.error = null));
    setEditData(nextEditData);
  };

  const runValidation = (validateAllFields?: boolean) => {
    const nextEditData = getClonedEditData();
    let hitCatch = false;

    try {
      yup
        .object(
          fields.reduce((acc, field) => {
            if (field.validation) acc[field.key] = field.validation;
            return acc;
          }, {})
        )
        .validateSync(getEditedDataObject(true).editedDataObjectForm, {
          abortEarly: false
        });
    } catch (errors) {
      nextEditData.forEach(ed => {
        let error = errors?.inner?.find(i => ed.key === i.path);

        if (error && (ed.edited || validateAllFields || startInEditMode)) {
          ed.error = error.errors.join(". ");
        } else if (!error) {
          ed.error = null;
        }
      });

      hitCatch = true;
    }

    if (!hitCatch) {
      nextEditData.forEach(ed => (ed.error = null));
    }

    setEditData(nextEditData);

    return !nextEditData.some(ed => ed.error);
  };

  const onEditClick = () => {
    if (onEditStarted) onEditStarted();
    setEditData(generateEditData());
    setIsEditing(true);
  };

  const getEditedValue = (value: any, dataType: string) => { // TODO: implementation is not very typescripty
    switch (dataType) {
      case DATA_TYPE_DROPDOWN2:
        if (Array.isArray(value)) {
          return value;
        } else if (typeof value == "string") {
          // This value could be a string if the dropdown was prepopulated and not edited
          // Even though its not edited, if we return undefined instead a populated drop down could show false positive validation
          return value;
        }
        return value?.value;
      default:
        return value;
    }
  };

  const getEditedDataObject = (returnAllFields?: boolean) => {
    let editedData = editData
      ?.filter(ed => ed.edited || returnAllFields)
      .map(ed => ({
        key: ed.key,
        value: getEditedValue(ed.value, ed.dataType),
        originalValue: ed.originalValue,
        error: ed.error // Error isnt used from this function anywhere in this file but this function is exposed through getEditedData and the user of that may want to know the error
      }))
      // One more filter to get rid of values that ultimately didnt change
      // (user must have edited them and then restored them to their original value)
      // not doing this in the first filter because we dont have the unnormalized value to compare to until after the map
      .filter(ed => ed.value != ed.originalValue || returnAllFields);

    let editedDataObjectForm = editedData?.reduce(
      (acc, ed) => ({ ...acc, [ed.key]: ed.value }),
      {}
    );

    return { editedData, editedDataObjectForm };
  };

  const callEditFinished = () => {
    let ed = getEditedDataObject();
    //explicit check for false because we dont want a return of undefined to count as not validated
    return onEditFinished ? onEditFinished(ed.editedData, ed.editedDataObjectForm) !== false : true;
  };

  const callEditChanged = () => {
    let ed = getEditedDataObject();
    if (onEditChanged) onEditChanged(ed.editedData, ed.editedDataObjectForm);
  };

  const onApplyClick = () => {
    let validated = true;

    if (!callEditFinished()) validated = false;

    if (validated) exitEditMode(false);
  };

  const exitEditMode = (callEditCancelled: boolean) => {
    if (onEditCancelled && callEditCancelled) onEditCancelled();
    setEditData(null);
    setIsEditing(false);
  };

  const getActions = () => {
    let myActions = [];
    if (isEditable && !onlyEditable) {
      if (isEditing) {
        let hasErrors = editData && editData.some(ed => ed.error);
        let hasEditedData = editData?.some(ed => ed.edited);
        myActions.push(
          {
            icon: hasErrors ? "playlist_add_check" : "check",
            helpText: hasErrors
              ? "Fix Validation Errors Before Saving"
              : "Save Changes",
            order: 9000,
            ...(!hasErrors && { onClick: onApplyClick })
          },
          {
            icon: "close",
            helpText: "Cancel Changes",
            order: 9001,
            onClick: () => hasEditedData ? dialog.showYesNo(null, "You have unsaved changes, are you sure you want to cancel editing?", () => exitEditMode(true), () => { }) : exitEditMode(true),
          }
        );
      } else {
        myActions.push({
          icon: "edit",
          helpText: "Edit",
          order: 9002,
          onClick: onEditClick
        });
      }
    }
    if (actions && actions.length > 0) {
      actions.forEach((action, index) => {
        myActions.push({
          key: index,
          ...action,
        });
      });
    }
    if (isCollapsible) {
      myActions.push({
        icon: isCollapsed ? "expand_more" : "expand_less",
        helpText: isCollapsed ? "Expand" : "Collapse",
        order: 100,
        onClick: () => setIsCollapsed(!isCollapsed),
      });
    }
    if (myActions.length) {
      myActions.sort((a, b) => a.order > b.order ? 1 : -1);
      return (
        <div className="mcard-edit-actions">{
          myActions.map((action, index) =>
            <MAction
              key={`action_${index}`}
              {...action}
            />)
        }</div>
      );
    }
  };

  const getMdCellClassName = () => {
    return cellWidth ? ` md-cell md-cell--${cellWidth}` : "";
  };

  let myFields;
  if (fields && !Array.isArray(fields[0])) {
    if (allowMultipleColumns) {
      let sliceIndex = Math.ceil(fields.length / 2);
      myFields = [[...fields.slice(0, sliceIndex)], [...fields.slice(sliceIndex)]];
    } else {
      myFields = [fields];
    }
  } else {
    myFields = fields;
  }

  let divStyle = myFields && { width: `${(1 / myFields.length) * 100}%` };

  let shouldRenderFields = true;

  if (
    !fields || // dont render if we dont have fields (duh)
    isFetching || // dont render if we are fetching
    // dont render if we are supposed to start in edit mode but dont have edit data yet
    // if we render before we have edit data then MFields data defaults to null
    ((onlyEditable || startInEditMode) && !editData)
  ) {
    shouldRenderFields = false;
  }

  let childrenComponent: ReactNode = null;

  if (!isFetching && !isCollapsed && children) {
    if (renderChildrenInDiv) {
      childrenComponent = <div className={cardContentClassName}>{children}</div>;
    } else {
      childrenComponent = <CardText className={cardContentClassName}>{children}</CardText>;
    }
  }

  return (
    <Card className={classes("mcard card-unnested", className, getMdCellClassName())}>
      <CardTitle title="" className="mcard-title">
        <div className="mcard-title-text" title={toolTipTitle}>{title}</div>
        {headerContent}
        {getActions()}
      </CardTitle>
      {isFetching && !isCollapsed && <MSpinner />}
      {shouldRenderFields && !isCollapsed && <CardText className={classes("mcard-fields-section", cardContentClassName)}>
        {myFields.map((f, i) => (
          <div key={`table_${i}`} style={divStyle}>
            {renderTable(f)}
          </div>
        ))}
      </CardText>}
      {childrenComponent}
    </Card>
  );
};

MCard.defaultProps = {
  tooltip: null,
  disableHoverStyles: false,
  startInEditMode: false,
  headerContent: null,
};

export default MCard;
